import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useApiConnection } from './hooks/useApiConnection';
import { NETWORKS, NETWORK_LIST, DEFAULT_NETWORK_ID } from './config/networks';
import CryptoPayPortalDirect from './components/CryptoPayPortalDirect';
import ManageSubscriptions from './components/ManageSubscriptions';
import PayLinkGenerator from './components/PayLinkGenerator';
import WidgetGenerator from './components/WidgetGenerator';
import UserAuth from './components/UserAuth';
import Landing from './components/Landing';
import { ethers } from 'ethers';
import { getProvider as getWalletProvider, onAccountsChanged, switchChain, disconnectWallet } from './lib/wallet';
const fmtAmt = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDateTime = (ts) => {
  const d = new Date(Number(ts) * 1000);
  if (isNaN(d.getTime())) return null;
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${dateStr}, ${timeStr}`;
};
const fmtPeriodRange = (payment) => {
  const ts = Number(payment?.timestamp);
  const start = !isNaN(ts) ? new Date(ts * 1000) : new Date(payment?.date);
  if (isNaN(start.getTime())) return null;
  const intervalSec = payment?.customer?.interval || 2592000;
  const end = new Date(start.getTime() + intervalSec * 1000);
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startStr} - ${endStr}`;
};

// Known stablecoin address → symbol. Seeded from network configs + common mainnet addresses.
const KNOWN_TOKEN_SYMBOLS = (() => {
  const map = {};
  // From network configs (all use USDC)
  for (const n of Object.values(NETWORKS)) {
    if (n.usdcAddress) map[n.usdcAddress.toLowerCase()] = 'USDC';
  }
  // Common mainnet/L2 stablecoins
  Object.assign(map, {
    '0xdac17f958d2ee523a2206206994597c13d831ec7': 'USDT', // Ethereum USDT
    '0x6b175474e89094c44da98b954eedeac495271d0f': 'DAI',  // Ethereum DAI
    '0xc2132d05d31c914a87c6611c10748aeb04b58e8f': 'USDT', // Polygon USDT
    '0x2791bca1f2de4661ed88a30c99a7a9449aa84174': 'USDC', // Polygon USDC
    '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9': 'USDT', // Arbitrum USDT
    '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8': 'USDC', // Arbitrum USDC (old)
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831': 'USDC', // Arbitrum USDC (native)
    '0x50c5725949a6f0c72e6c4a641f24049a917db0cb': 'DAI',  // Base DAI
    '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': 'USDC', // Base USDC
  });
  return map;
})();

const resolveTokenSymbol = (address) => {
  if (!address) return 'USDC';
  return KNOWN_TOKEN_SYMBOLS[address.toLowerCase()] ?? (address.slice(0, 6) + '…');
};

// ─── SPARKLINE HELPERS ────────────────────────────────────────────────────────
function buildMetricHistory(customers, points = 20) {
  if (!customers || customers.length === 0) return null;
  const nowTs = Math.floor(Date.now() / 1000);
  const earliestTs = Math.min(...customers.map(c => c.createdTimestamp));
  const windowStart = Math.max(earliestTs - 86400, nowTs - 90 * 86400);
  if (nowTs - windowStart < 3600) return null;

  const step = (nowTs - windowStart) / (points - 1);

  return Array.from({ length: points }, (_, i) => {
    const t = windowStart + i * step;
    let activeSubs = 0, mrr = 0, totalSubs = 0, cancelled = 0, totalEstPaid = 0;

    for (const c of customers) {
      if (c.createdTimestamp > t) continue;
      totalSubs++;
      const isActive = !c.endTimestamp || c.endTimestamp > t;
      if (isActive) {
        activeSubs++;
        mrr += c.amount * (30 * 86400) / (c.interval || 1);
      } else {
        cancelled++;
      }
      const age = t - c.createdTimestamp;
      const totalAge = nowTs - c.createdTimestamp;
      totalEstPaid += totalAge > 0 ? c.lifetime * Math.min(age / totalAge, 1) : 0;
    }

    return {
      t,
      activeSubs,
      mrr,
      churnRate: totalSubs > 0 ? (cancelled / totalSubs) * 100 : 0,
      ltv: totalSubs > 0 ? totalEstPaid / totalSubs : 0,
    };
  });
}

function computeDailyChanges(history) {
  if (!history || history.length < 2) return null;
  const last = history[history.length - 1];
  const target = last.t - 7 * 86400;
  let prev = history[0];
  for (const p of history) {
    if (Math.abs(p.t - target) < Math.abs(prev.t - target)) prev = p;
  }
  if (prev === last) prev = history[history.length - 2];
  const pct = (cur, pre) => pre === 0 ? (cur > 0 ? 100 : 0) : ((cur - pre) / Math.abs(pre)) * 100;
  return {
    mrr: pct(last.mrr, prev.mrr),
    activeSubs: pct(last.activeSubs, prev.activeSubs),
    churnRate: pct(last.churnRate, prev.churnRate),
    ltv: pct(last.ltv, prev.ltv),
  };
}

function DeltaBadge({ delta, invert = false }) {
  if (delta === null || delta === undefined || isNaN(delta)) return <span className="text-white/30 text-sm">—</span>;
  const neutral = Math.abs(delta) < 0.05;
  const good = neutral ? null : (invert ? delta < 0 : delta > 0);
  const cls = neutral ? 'text-white/35' : good ? 'text-emerald-400' : 'text-rose-400';
  return <span className={`${cls} text-sm font-medium`}>{delta > 0 ? '+' : ''}{delta.toFixed(1)}% this week</span>;
}

function Sparkline({ series, timestamps, color, gradientId, formatY }) {
  if (!series || series.length < 2) return null;

  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || Math.max(max, 1);

  // SVG coordinate space
  const VW = 240, VH = 104;
  const yGutter = 46;  // left space for Y-axis labels
  const xGutter = 18;  // bottom space for X-axis labels
  const padTop = 16;
  const padRight = 6;

  const cX = yGutter;
  const cY = padTop;
  const cW = VW - yGutter - padRight;
  const cH = VH - xGutter - padTop;

  const toX = (i) => cX + (i / (series.length - 1)) * cW;
  const toY = (v) => cY + cH - ((v - min) / range) * cH;

  const pts = series.map((v, i) => ({ x: toX(i), y: toY(v) }));
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${toX(series.length - 1)},${cY + cH} L${cX},${cY + cH} Z`;

  const yTicks = [min, min + range / 2, max];
  const xTickIdxs = [0, Math.floor((series.length - 1) / 2), series.length - 1];
  const fmtDate = (ts) => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const fmtY = formatY || ((v) => String(Math.round(v)));

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} overflow="visible" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Horizontal gridlines + Y labels */}
      {yTicks.map((v, i) => {
        const y = toY(v);
        return (
          <g key={i}>
            <line x1={cX} y1={y} x2={cX + cW} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth="0.5" />
            <text x={cX - 5} y={y} textAnchor="end" dominantBaseline="middle" fontSize="10" fill="rgba(255,255,255,0.7)">
              {fmtY(v)}
            </text>
          </g>
        );
      })}

      {/* Y-axis rule */}
      <line x1={cX} y1={cY} x2={cX} y2={cY + cH} stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />

      {/* X-axis baseline */}
      <line x1={cX} y1={cY + cH} x2={cX + cW} y2={cY + cH} stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />

      {/* X tick marks + labels */}
      {timestamps && xTickIdxs.map((idx, i) => {
        const x = toX(idx);
        const anchor = i === 0 ? 'start' : i === xTickIdxs.length - 1 ? 'end' : 'middle';
        return (
          <g key={i}>
            <line x1={x} y1={cY + cH} x2={x} y2={cY + cH + 3} stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
            <text x={x} y={VH - 2} textAnchor={anchor} dominantBaseline="auto" fontSize="10" fill="rgba(255,255,255,0.35)">
              {fmtDate(timestamps[idx])}
            </text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={areaPath} fill={`url(#${gradientId})`} />

      {/* Line */}
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

      {/* Endpoint dot */}
      <circle cx={toX(series.length - 1).toFixed(1)} cy={toY(series[series.length - 1]).toFixed(1)} r="2" fill={color} />
    </svg>
  );
}

// Contract ABIs
const SUBS_ABI = [
  "function processPayment(bytes32 subscriptionId) external",
  "function cancelSubscription(bytes32 subscriptionId) external",
  "function initiateCancellation(bytes32 subscriptionId) external",
  "function withdrawCancellationNotice(bytes32 subscriptionId) external",
  "function refundPayment(bytes32 subscriptionId, uint256 amount) external",
  "function getSubscription(bytes32 subscriptionId) view returns (address customer, address merchant, uint256 amountPerCycle, uint256 interval, uint256 lastPayment, uint256 nextPayment, bool active)",
  "function cancellationNoticeSentAt(bytes32 subscriptionId) view returns (uint256)",
  "event SubscriptionPaid(bytes32 indexed subscriptionId, address indexed customer, address indexed merchant, uint256 amount, uint256 feeAmount, uint256 timestamp)",
  "event SubscriptionCancelled(bytes32 indexed subscriptionId, address indexed customer, address indexed merchant)",
  "event RefundProcessed(bytes32 indexed subscriptionId, address indexed customer, address indexed merchant, uint256 amount, uint256 timestamp)"
];

const USDC_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)"
];

// ─── ADDRESS CELL: truncated display, full address on hover, click to copy ───
function AddressCell({ address, fieldKey, copiedField, onCopy }) {
  if (!address) return <span className="text-sm text-white/70">—</span>;
  const isCopied = copiedField === fieldKey;
  return (
    <div className="relative group text-right">
      <span
        onClick={() => onCopy(address, fieldKey)}
        className="text-sm text-white/70 cursor-pointer hover:text-white transition-colors"
      >
        {address.slice(0, 6)}...{address.slice(-4)}
      </span>
      <div className={`absolute right-0 bottom-full mb-1.5 px-2.5 py-1.5 bg-[#161b22] border rounded-lg whitespace-nowrap text-xs font-mono pointer-events-none z-50 transition-opacity ${
        isCopied
          ? 'opacity-100 border-emerald-500/30 text-emerald-400'
          : 'opacity-0 group-hover:opacity-100 border-white/[0.08] text-white'
      }`}>
        {isCopied ? 'Copied!' : address}
      </div>
    </div>
  );
}

// ─── USDC BALANCE DISPLAY COMPONENT ─────────────────────────────
function USDCBalance({ address, label, rpcUrl, usdcAddress, hideCurrency = false, valueClassName }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const valueCls = valueClassName ?? 'font-semibold text-white text-sm';
  const mutedCls = valueClassName ?? 'text-white/50 text-sm';

  useEffect(() => {
    if (!usdcAddress || !rpcUrl || !ethers.isAddress(usdcAddress)) {
      setLoading(false);
      return;
    }

    async function fetchBalance() {
      try {
        const provider = new ethers.JsonRpcProvider(rpcUrl);
        const usdc = new ethers.Contract(usdcAddress, USDC_ABI, provider);
        const bal = await usdc.balanceOf(address);
        setBalance(ethers.formatUnits(bal, 6));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching USDC balance:', err);
        setLoading(false);
      }
    }
    fetchBalance();

    // Refresh every 5 seconds
    const interval = setInterval(fetchBalance, 5000);
    return () => clearInterval(interval);
  }, [address, rpcUrl, usdcAddress]);

  if (!usdcAddress || !rpcUrl) {
    return <span className={mutedCls}>—</span>;
  }

  if (loading) {
    return <span className={mutedCls}>···</span>;
  }

  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-xs text-white/40">{label}</span>}
      <span className={valueCls}>
        ${balance ? fmtAmt(balance) : '0.00'}{hideCurrency ? '' : ' USDC'}
      </span>
    </div>
  );
}

// ─── SPARKLINE ───────────────────────────────────────────────

// ─── PAYMENT PAGE WRAPPER ────────────────────────────────────
function PaymentPageView({ onBack, onSubscriptionCreated, merchantAddress, subsAddress, usdcAddress, chainId }) {
  // Parse payment link from URL hash
  const getPaymentLinkData = () => {
    const hash = window.location.hash;
    if (hash.includes('?')) {
      const params = new URLSearchParams(hash.split('?')[1]);
      return {
        plan:     params.get('plan'),
        features: params.get('features'),
        interval: params.get('interval'),
        merchant: params.get('merchant'),
        contract: params.get('contract')
      };
    }
    return null;
  };

  const paymentLinkData = getPaymentLinkData();

  return (
    <div>
      <div className="bg-black/50 backdrop-blur-xl border-b border-white/[0.07] px-6 py-2 flex items-center">
        <button
          onClick={onBack}
          className="text-sm text-white/60 hover:text-white font-medium flex items-center gap-1 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Dashboard
        </button>
      </div>
      <CryptoPayPortalDirect
        subsAddress={subsAddress}
        merchantAddress={merchantAddress}
        usdcAddress={usdcAddress}
        chainId={chainId}
        merchantName="Crypto Subscriptions"
        onSubscriptionCreated={(data) => {
          console.log('New subscription created:', data);
          if (onSubscriptionCreated) onSubscriptionCreated(data);
          setTimeout(() => {
            onBack();
          }, 3000);
        }}
        paymentLinkData={paymentLinkData}
      />
    </div>
  );
}

// ─── MANAGE PAGE WRAPPER ─────────────────────────────────────
function ManagePortalView({ onBack }) {
  const hash = window.location.hash;
  const params = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : new URLSearchParams();
  const defaultNetworkId    = params.get('network') || DEFAULT_NETWORK_ID;
  const defaultSubscriptionId = params.get('subscription') || null;

  return (
    <div>
      <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center">
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-800 font-medium flex items-center gap-1 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back
        </button>
      </div>
      <ManageSubscriptions
        defaultNetworkId={defaultNetworkId}
        defaultSubscriptionId={defaultSubscriptionId}
      />
    </div>
  );
}

// ─── ALERT COMPONENTS (TOAST STYLE) ─────────────────────────
function SuccessAlert({ message, onDismiss }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onDismiss();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, onDismiss]);

  if (!message) return null;

  return (
    <div className="fixed top-4 left-4 right-4 sm:left-auto z-50 animate-fadeIn">
      <div className="bg-[#0e1117] border border-white/[0.1] rounded-xl shadow-xl px-5 py-4 flex items-center gap-3 sm:min-w-[320px] max-w-md sm:ml-auto">
        <div className="w-6 h-6 bg-emerald-500/15 rounded-full flex items-center justify-center flex-shrink-0">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <span className="flex-1 text-white/80 text-sm">{message}</span>
      </div>
    </div>
  );
}

function ErrorAlert({ error, onDismiss }) {
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        onDismiss();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, onDismiss]);

  if (!error) return null;

  const getErrorMessage = (err) => {
    if (typeof err === 'string') return err;
    if (err.message) {
      // Parse common error messages
      if (err.message.includes('user rejected')) return 'Transaction Cancelled';
      if (err.message.includes('insufficient funds')) return 'Insufficient Balance';
      if (err.message.includes('Too early')) return 'Payment Not Due Yet';
      if (err.message.includes('not active')) return 'Subscription Not Active';
      if (err.message.includes('Only customer or merchant')) return 'Permission Denied';
      return err.message;
    }
    return 'An error occurred';
  };

  return (
    <div className="fixed top-4 left-4 right-4 sm:left-auto z-50 animate-fadeIn">
      <div className="bg-[#0e1117] border border-white/[0.1] rounded-xl shadow-xl px-5 py-4 flex items-center gap-3 sm:min-w-[320px] max-w-md sm:ml-auto">
        <div className="w-6 h-6 bg-red-500/15 rounded-full flex items-center justify-center flex-shrink-0">
          <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-red-400"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
        </div>
        <span className="flex-1 text-white/80 text-sm">{getErrorMessage(error)}</span>
      </div>
    </div>
  );
}


function getPlanColor(plan) {
  const colors = {
    'Basic': 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/25 px-2.5 py-0.5 rounded-full text-xs font-medium',
    'Pro': 'bg-violet-500/15 text-violet-400 border border-violet-500/25 px-2.5 py-0.5 rounded-full text-xs font-medium',
    'Premium': 'bg-amber-500/15 text-amber-400 border border-amber-500/25 px-2.5 py-0.5 rounded-full text-xs font-medium',
  };
  return colors[plan] || 'bg-white/[0.08] text-white/60 border border-white/[0.1] px-2.5 py-0.5 rounded-full text-xs font-medium';
}

function getStatusColor(status) {
  switch (status) {
    case 'active':
    case 'success': return 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2.5 py-0.5 rounded-full text-xs font-medium';
    case 'past_due':
    case 'created': return 'text-amber-400 bg-amber-500/10 border border-amber-500/25 px-2.5 py-0.5 rounded-full text-xs font-medium';
    case 'cancelled': return 'text-white/40 bg-white/[0.05] border border-white/[0.1] px-2.5 py-0.5 rounded-full text-xs font-medium';
    case 'failed': return 'text-red-400 bg-red-500/10 border border-red-500/25 px-2.5 py-0.5 rounded-full text-xs font-medium';
    default: return 'text-white/40 bg-white/[0.05] border border-white/[0.1] px-2.5 py-0.5 rounded-full text-xs font-medium';
  }
}

// ─── WEBHOOK GUIDE COMPONENTS ────────────────────────────────
function PayloadReference({ samplePayload, payloadCopied, onCopy }) {
  return (
    <div className="mt-6 pt-5 border-t border-white/[0.07]">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-medium text-white/35 uppercase tracking-widest">Payload Reference</p>
        <button onClick={onCopy} className="text-[11px] text-cyan-400 hover:text-cyan-300 transition-colors">
          {payloadCopied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-xs text-white/70 font-mono overflow-x-auto leading-relaxed">{samplePayload}</pre>
      <div className="mt-3 space-y-1.5 text-xs text-white/40 leading-relaxed">
        <p>• <span className="font-mono text-white/60">email</span> is <span className="font-mono text-white/60">null</span> if the subscriber skipped the email field at checkout.</p>
        <p>• Events: <span className="font-mono text-white/60">subscription.paid</span> · <span className="font-mono text-white/60">subscription.cancelled</span> · <span className="font-mono text-white/60">subscription.payment_failed</span></p>
      </div>
    </div>
  );
}

function ZapierGuide({ samplePayload, payloadCopied, onCopy }) {
  const steps = [
    {
      n: 1,
      title: 'Create a Catch Hook trigger in Zapier',
      body: <>Go to <span className="text-white">zapier.com</span> → <strong className="text-white">Create Zap</strong> → search for <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">Webhooks by Zapier</span> → select <strong className="text-white">Catch Hook</strong>. Zapier will generate a unique webhook URL.</>
    },
    {
      n: 2,
      title: 'Paste the URL into FlowSub',
      body: <>Copy the webhook URL from Zapier and paste it into the <strong className="text-white">Webhook URL</strong> field in FlowSub's Notification Settings. Save your settings.</>
    },
    {
      n: 3,
      title: 'Send a test event',
      body: <>Back in Zapier, click <strong className="text-white">Test trigger</strong> — Zapier will wait for an incoming request. Then trigger a test subscription on your payment page. Zapier will capture the payload and show all available fields.</>
    },
    {
      n: 4,
      title: 'Add a Filter step',
      body: <>Click <strong className="text-white">+</strong> to add a <strong className="text-white">Filter</strong> step. Set the condition: <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">event</span> · Text · Exactly matches · <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">subscription.paid</span>. This ensures only new payments reach your newsletter platform — not cancellations or failed payments.</>
    },
    {
      n: 5,
      title: 'Add your action',
      body: <>Click <strong className="text-white">+</strong> to add an action. Choose your platform — <strong className="text-white">Mailchimp</strong>, <strong className="text-white">Beehiiv</strong>, or <strong className="text-white">Kit</strong> — and select <strong className="text-white">Add/Update Subscriber</strong>. Map the <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">email</span> field from the FlowSub payload to the subscriber email field.</>
    },
    {
      n: 6,
      title: 'Handle cancellations (optional)',
      body: <>Create a second Zap using the same webhook URL. Add a <strong className="text-white">Filter</strong>: <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">event</span> exactly matches <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">subscription.cancelled</span>. Then add an action to archive or untag the subscriber in your platform.</>
    },
    {
      n: 7,
      title: 'Turn on your Zap',
      body: <>Publish the Zap. Every FlowSub payment will now automatically add the subscriber to your list — no manual work required.</>
    },
  ];
  return (
    <div className="space-y-5">
      <p className="text-sm text-white/60 leading-relaxed">Zapier connects FlowSub to 7,000+ apps. Setup takes about 10 minutes and requires no code.</p>
      <div className="space-y-4">
        {steps.map(({ n, title, body }) => (
          <div key={n} className="flex gap-4">
            <div className="w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-semibold text-cyan-400">{n}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-white mb-1">{title}</p>
              <p className="text-sm text-white/50 leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
      </div>
      <PayloadReference samplePayload={samplePayload} payloadCopied={payloadCopied} onCopy={onCopy} />
    </div>
  );
}

function MakeGuide({ samplePayload, payloadCopied, onCopy }) {
  const steps = [
    {
      n: 1,
      title: 'Create a scenario in Make',
      body: <>Go to <span className="text-white">make.com</span> → <strong className="text-white">Create a new scenario</strong> → click the empty module → search for <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">Webhooks</span> → select <strong className="text-white">Custom webhook</strong> → click <strong className="text-white">Add a webhook</strong> to generate a URL.</>
    },
    {
      n: 2,
      title: 'Paste the URL into FlowSub',
      body: <>Copy the webhook URL from Make and paste it into the <strong className="text-white">Webhook URL</strong> field in FlowSub's Notification Settings. Save your settings.</>
    },
    {
      n: 3,
      title: 'Capture the data structure',
      body: <>Back in Make, click <strong className="text-white">Run once</strong> — Make will wait for an incoming request. Trigger a test subscription on your payment page. Make will capture the payload and make all fields available for mapping.</>
    },
    {
      n: 4,
      title: 'Add a filter',
      body: <>Click the route line between the webhook and the next module, then click <strong className="text-white">Add a filter</strong>. Set the condition: <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">event</span> · Equal to · <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">subscription.paid</span>. This ensures only payments reach your newsletter platform.</>
    },
    {
      n: 5,
      title: 'Add your action module',
      body: <>Click <strong className="text-white">+</strong> to add a module for your newsletter platform — <strong className="text-white">Mailchimp</strong>, <strong className="text-white">Beehiiv</strong>, or <strong className="text-white">Kit</strong>. Choose <strong className="text-white">Add/Update a Subscriber</strong> and map the <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">email</span> field from the FlowSub payload to the subscriber email field.</>
    },
    {
      n: 6,
      title: 'Handle cancellations (optional)',
      body: <>Duplicate the scenario. Update the filter condition to <span className="font-mono text-cyan-400 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">subscription.cancelled</span> and replace the action with an archive or untag step.</>
    },
    {
      n: 7,
      title: 'Activate your scenario',
      body: <>Click the <strong className="text-white">ON/OFF toggle</strong> at the bottom of the screen to activate. Make will process every incoming FlowSub event automatically.</>
    },
  ];
  return (
    <div className="space-y-5">
      <p className="text-sm text-white/60 leading-relaxed">Make (formerly Integromat) offers more advanced logic than Zapier and a generous free tier.</p>
      <div className="space-y-4">
        {steps.map(({ n, title, body }) => (
          <div key={n} className="flex gap-4">
            <div className="w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-semibold text-cyan-400">{n}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-white mb-1">{title}</p>
              <p className="text-sm text-white/50 leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
      </div>
      <PayloadReference samplePayload={samplePayload} payloadCopied={payloadCopied} onCopy={onCopy} />
    </div>
  );
}

// ─── WEBHOOK GUIDE STANDALONE PAGE ───────────────────────────
function WebhookGuidePage() {
  const [platform, setPlatform] = useState('zapier');
  const [payloadCopied, setPayloadCopied] = useState(false);
  const samplePayload = JSON.stringify({
    event: 'subscription.paid',
    subscriptionId: '0xabc...123',
    wallet: '0xDEF...456',
    email: 'subscriber@example.com',
    plan: 'Premium Newsletter',
    amount: 10.00,
    network: 'base',
    timestamp: 1744320000,
  }, null, 2);
  function copyPayload() {
    navigator.clipboard.writeText(samplePayload);
    setPayloadCopied(true);
    setTimeout(() => setPayloadCopied(false), 1500);
  }
  const tabs = [{ id: 'zapier', label: 'Zapier' }, { id: 'make', label: 'Make' }];
  return (
    <div className="min-h-screen bg-[#080c10] flex items-start justify-center p-6 pt-12">
      <div className="bg-[#0e1117] border border-white/[0.08] rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="px-7 pt-6 pb-4 border-b border-white/[0.07]">
          <h2 className="text-lg font-semibold text-white">Webhook Setup Guide</h2>
          <p className="text-sm text-white/50 mt-0.5">Connect FlowSub to your newsletter platform</p>
        </div>
        <div className="flex border-b border-white/[0.07]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setPlatform(tab.id)}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                platform === tab.id ? 'text-white border-cyan-400' : 'text-white/40 hover:text-white/70 border-transparent'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="px-7 py-6">
          {platform === 'zapier' && <ZapierGuide samplePayload={samplePayload} payloadCopied={payloadCopied} onCopy={copyPayload} />}
          {platform === 'make' && <MakeGuide samplePayload={samplePayload} payloadCopied={payloadCopied} onCopy={copyPayload} />}
        </div>
      </div>
    </div>
  );
}

// ─── NOTIFICATION SETTINGS MODAL ─────────────────────────────
function NotificationSettingsModal({ isOpen, onClose, telegramChatId, setTelegramChatId, editingTelegram, setEditingTelegram, webhookUrl, setWebhookUrl, editingWebhook, setEditingWebhook, substackPublication, setSubstackPublication, substackToken, setSubstackToken, substackTokenValid, substackLastSynced, substackTesting, substackTestResult, onTestSubstack, notifyPayments, setNotifyPayments, notifyCancels, setNotifyCancels, onSave, saving, saved }) {
  const [botUsername, setBotUsername] = useState(null);

  useEffect(() => {
    if (!isOpen || botUsername !== null) return;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    fetch(`${apiUrl}/api/telegram-bot`)
      .then(r => r.json())
      .then(d => setBotUsername(d.username || ''))
      .catch(() => setBotUsername(''));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { e.currentTarget._mbdown = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.currentTarget._mbdown) onClose(); }}
    >
      <div
        className="bg-[#0e1117] border border-white/[0.08] rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-4 border-b border-white/[0.07] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Notifications</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] rounded-lg text-white/40 hover:text-white transition-all"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">

          {/* Substack */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-white/80">Substack</label>
                <p className="text-xs text-white/40 mt-0.5">Add paying subscribers to your Substack automatically.</p>
              </div>
              {substackTokenValid === true && (
                <span className="flex items-center gap-1.5 text-xs text-emerald-400 shrink-0 ml-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  Connected{substackLastSynced ? ` · ${new Date(substackLastSynced).toLocaleDateString()}` : ''}
                </span>
              )}
              {substackTokenValid === false && (
                <span className="flex items-center gap-1.5 text-xs text-red-400 shrink-0 ml-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
                  Token invalid
                </span>
              )}
            </div>

            <div className="flex items-center bg-white/[0.05] border border-white/[0.1] rounded-xl overflow-hidden focus-within:border-cyan-500/50 transition-all">
              <input
                type="text"
                value={substackPublication}
                onChange={(e) => setSubstackPublication(e.target.value.trim())}
                placeholder="yourpub"
                className="flex-1 min-w-0 bg-transparent text-white/80 placeholder:text-white/30 outline-none px-4 py-2.5 text-sm font-[inherit]"
              />
              <span className="text-xs sm:text-sm text-white/30 pr-3 sm:pr-4 shrink-0">.substack.com</span>
            </div>

            <div className="space-y-1">
              <input
                type="password"
                value={substackToken}
                onChange={(e) => setSubstackToken(e.target.value)}
                placeholder={substackTokenValid !== null ? '••••••••  (paste new token to update)' : 'Session token (substack.sid cookie)'}
                className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 placeholder:text-white/30 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-sm font-[inherit] transition-all"
              />
              <p className="text-xs text-white/30">
                Log in to Substack.com → DevTools (F12) → Application → Cookies → https://substack.com → <span className="text-white/50">substack.sid</span>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onTestSubstack}
                disabled={substackTesting || !substackPublication}
                className="text-xs px-3 py-1.5 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/60 hover:text-white rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {substackTesting ? (
                  <><div className="w-3 h-3 border border-white/40 border-t-transparent rounded-full animate-spin" />Testing...</>
                ) : 'Test connection'}
              </button>
              {substackTestResult && (
                <span className={`text-xs ${substackTestResult === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {substackTestResult === 'ok' ? 'Connected ✓' : substackTestResult}
                </span>
              )}
            </div>
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* Telegram */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">Telegram</label>
              <p className="text-xs text-white/40 mb-2">Get a message when something happens on your account.</p>
              {!editingTelegram ? (
                <div className="flex items-center justify-between w-full bg-white/[0.03] border border-white/[0.07] rounded-xl px-4 py-2.5">
                  <span className="text-sm text-white/70">{telegramChatId}</span>
                  <button onClick={() => setEditingTelegram(true)} className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors shrink-0 ml-3">Edit</button>
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    placeholder="Your Telegram chat ID (e.g. 123456789)"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 placeholder:text-white/30 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-sm font-[inherit] transition-all"
                  />
                  <div className="text-xs text-white/35 space-y-1">
                    {botUsername
                      ? <p>1. <a href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">Start @{botUsername}</a> — this lets the bot message you.</p>
                      : <p>1. Start a chat with the FlowSub Telegram bot — this lets the bot message you.</p>
                    }
                    <p>2. Open <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">@userinfobot</a> and send any message — it will instantly reply with your numeric chat ID.</p>
                    <p>3. Paste that number above and save.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2.5">
              <p className="text-xs text-white/40">Notify me when...</p>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-white/70">A payment is received</span>
                <div
                  onClick={() => setNotifyPayments(!notifyPayments)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${notifyPayments ? 'bg-cyan-500' : 'bg-white/[0.1]'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifyPayments ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-white/70">A subscription is cancelled or fails</span>
                <div
                  onClick={() => setNotifyCancels(!notifyCancels)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${notifyCancels ? 'bg-cyan-500' : 'bg-white/[0.1]'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notifyCancels ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </label>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white rounded-xl px-6 py-2.5 text-sm font-semibold transition-all"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || saved}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold rounded-xl px-6 py-2.5 text-sm transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saved ? 'Saved!' : saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────
export default function App() {
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const [currentHash, setCurrentHash] = useState(() => window.location.hash);
  useEffect(() => {
    const onHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const [user, setUser] = useState(() => {
    try { const s = localStorage.getItem('userSession'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [showLanding, setShowLanding] = useState(() => {
    try { return !localStorage.getItem('userSession'); } catch { return true; }
  });
  const [selectedNetworkId, setSelectedNetworkId] = useState(() => {
    try { const s = localStorage.getItem('selectedNetworkId'); return (s && NETWORKS[s]) ? s : DEFAULT_NETWORK_ID; }
    catch { return DEFAULT_NETWORK_ID; }
  });
  const network = NETWORKS[selectedNetworkId];
  useEffect(() => { try { localStorage.setItem('selectedNetworkId', selectedNetworkId); } catch {} }, [selectedNetworkId]);

  const selectedToken = 'USDC';

  const [view, setView] = useState('dashboard');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelNoticeAt, setCancelNoticeAt] = useState(null); // null = none/unchecked, number = on-chain timestamp
  const [cancelNoticeLoading, setCancelNoticeLoading] = useState(false);
  const [withdrawingNotice, setWithdrawingNotice] = useState(false);
  const [refundStep, setRefundStep] = useState(null); // null | 'approving' | 'refunding'
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  const [forceReload, setForceReload] = useState(0);
  const [payLinkModalOpen, setPayLinkModalOpen] = useState(false);
  const [widgetModalOpen, setWidgetModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTelegramChatId, setSettingsTelegramChatId] = useState('');
  const [settingsNotifyPayments, setSettingsNotifyPayments] = useState(true);
  const [settingsNotifyCancels, setSettingsNotifyCancels] = useState(true);
  const [settingsWebhookUrl, setSettingsWebhookUrl] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsEditingTelegram, setSettingsEditingTelegram] = useState(true);
  const [settingsEditingWebhook, setSettingsEditingWebhook] = useState(true);
  const [settingsSubstackPublication, setSettingsSubstackPublication] = useState('');
  const [settingsSubstackToken, setSettingsSubstackToken] = useState('');
  const [settingsSubstackTokenValid, setSettingsSubstackTokenValid] = useState(null);
  const [settingsSubstackLastSynced, setSettingsSubstackLastSynced] = useState(null);
  const [settingsSubstackTesting, setSettingsSubstackTesting] = useState(false);
  const [settingsSubstackTestResult, setSettingsSubstackTestResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const copyField = useCallback((text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  }, []);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, cancelled, overdue
  const [planFilter, setPlanFilter] = useState('all'); // all or plan name string
  const [subsMonth, setSubsMonth] = useState('all'); // all, 0-11
  const [subsYear, setSubsYear] = useState('all'); // all, 2024, 2025, 2026
  
  // Pagination for subscriptions
  const [subsCurrentPage, setSubsCurrentPage] = useState(1);
  const subsPerPage = 10; // Show 10 subscriptions per page for optimal performance
  
  // Activity filters
  const [activityTypeFilter, setActivityTypeFilter] = useState('all'); // all, subscription, payment, cancelled
  const [activityMonth, setActivityMonth] = useState('all'); // all, 0-11
  const [activityYear, setActivityYear] = useState('all'); // all, 2024, 2025, 2026
  const [activitySearch, setActivitySearch] = useState('');
  
  // Pagination for recent activity
  const [activityCurrentPage, setActivityCurrentPage] = useState(1);
  const activityPerPage = 10;

  const handleLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('userSession');
    disconnectWallet();
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  async function openSettings() {
    setSettingsSaved(false);
    setSettingsEditingTelegram(true);
    setSettingsEditingWebhook(true);
    setSettingsSubstackToken('');
    setSettingsSubstackTestResult(null);
    setSettingsOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/merchants/${merchantAddress}`);
      if (res.ok) {
        const data = await res.json();
        setSettingsTelegramChatId(data.telegramChatId ?? '');
        setSettingsNotifyPayments(data.notifyPayments ?? true);
        setSettingsNotifyCancels(data.notifyCancels ?? true);
        setSettingsWebhookUrl(data.webhookUrl ?? '');
        setSettingsEditingTelegram(!data.telegramChatId);
        setSettingsEditingWebhook(!data.webhookUrl);
        setSettingsSubstackPublication(data.substackPublication ?? '');
        setSettingsSubstackTokenValid(data.substackTokenValid);
        setSettingsSubstackLastSynced(data.substackLastSyncedAt);
      }
    } catch { /* first-time merchant, defaults are fine */ }
  }

  async function saveSettings() {
    setSettingsSaving(true);
    try {
      const body = {
        telegramChatId: settingsTelegramChatId || null,
        webhookUrl: settingsWebhookUrl || null,
        substackPublication: settingsSubstackPublication || null,
        notifyPayments: settingsNotifyPayments,
        notifyCancels: settingsNotifyCancels,
      };
      if (settingsSubstackToken) body.substackSessionToken = settingsSubstackToken;
      await fetch(`${API_URL}/api/merchants/${merchantAddress}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch { /* non-fatal */ }
    setSettingsSaving(false);
    setSettingsSaved(true);
    setSettingsEditingTelegram(false);
    setSettingsEditingWebhook(false);
    setTimeout(() => { setSettingsSaved(false); setSettingsOpen(false); }, 1200);
  }

  async function testSubstackToken() {
    setSettingsSubstackTesting(true);
    setSettingsSubstackTestResult(null);
    try {
      const body = {};
      if (settingsSubstackToken) body.substackSessionToken = settingsSubstackToken;
      if (settingsSubstackPublication) body.substackPublication = settingsSubstackPublication;
      const res = await fetch(`${API_URL}/api/merchants/${merchantAddress}/substack/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setSettingsSubstackTestResult(data.ok ? 'ok' : (data.error || 'Failed'));
    } catch (err) {
      setSettingsSubstackTestResult(err.message);
    }
    setSettingsSubstackTesting(false);
  }

  // Keep merchantAddress in sync with MetaMask's active account.
  // Use a ref so the listener is registered once and always reads the latest user value.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    const handleAccountsChanged = (accounts) => {
      const currentUser = userRef.current;
      if (!currentUser) return;
      if (accounts.length === 0) {
        handleLogout();
      } else if (accounts[0].toLowerCase() !== currentUser.address.toLowerCase()) {
        const updated = { ...currentUser, address: accounts[0] };
        localStorage.setItem('userSession', JSON.stringify(updated));
        setUser(updated);
      }
    };

    return onAccountsChanged(handleAccountsChanged);
  }, [handleLogout]); // handleLogout is stable (useCallback with [] deps)

  async function ensureCorrectChain(targetChainId) {
    try {
      await switchChain(targetChainId);
    } catch (e) {
      if (e.code === 4902) throw new Error(`Add ${network.name} to your wallet first.`);
      throw e;
    }
  }

  const SUBS_ADDRESS = network.subsAddress;
  const USDC_ADDRESS = network.usdcAddress;
  const merchantAddress = user?.address ?? '';

  const { customers, recentActivity: chainActivity = [], loading, error: hookError } = useApiConnection(
    network,
    merchantAddress,
    forceReload
  );

  const recentActivity = chainActivity;

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId) ?? null;
  const setSelectedCustomer = (val) => setSelectedCustomerId(val ? val.id : null);

  // Filter customers
  const filteredCustomers = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const base = customers.filter(c => resolveTokenSymbol(c.tokenAddress) === selectedToken);
    return base.filter(customer => {
      const matchesSearch = customer.id.toLowerCase().includes(searchLower) ||
                           customer.wallet.toLowerCase().includes(searchLower) ||
                           customer.plan.toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === 'all' ||
                           (statusFilter === 'active' && customer.status === 'active') ||
                           (statusFilter === 'cancelled' && customer.status === 'cancelled') ||
                           (statusFilter === 'overdue' && customer.status === 'past_due');
      const matchesPlan = planFilter === 'all' || customer.plan === planFilter;
      let matchesDate = true;
      if (subsMonth !== 'all' || subsYear !== 'all') {
        const startDate = new Date(customer.startDate);
        if (!isNaN(startDate.getTime())) {
          if (subsMonth !== 'all' && startDate.getMonth() !== parseInt(subsMonth)) matchesDate = false;
          if (subsYear !== 'all' && startDate.getFullYear() !== parseInt(subsYear)) matchesDate = false;
        } else {
          matchesDate = false;
        }
      }
      return matchesSearch && matchesStatus && matchesPlan && matchesDate;
    }).sort((a, b) => (b.createdTimestamp || 0) - (a.createdTimestamp || 0));
  }, [customers, selectedToken, searchTerm, statusFilter, planFilter, subsMonth, subsYear]);

  // Customer pool filtered to the selected stablecoin (for metrics + sparklines + activity)
  const tokenFilteredCustomers = useMemo(() =>
    customers.filter(c => resolveTokenSymbol(c.tokenAddress) === selectedToken),
    [customers, selectedToken]
  );

  const tokenFilteredIds = useMemo(() =>
    new Set(tokenFilteredCustomers.map(c => c.id)),
    [tokenFilteredCustomers]
  );

  // Metrics always computed from the filtered pool
  const displayMetrics = useMemo(() => {
    const active = tokenFilteredCustomers.filter(c => c.status === 'active' || c.status === 'past_due');
    const cancelled = tokenFilteredCustomers.filter(c => c.status === 'cancelled');
    const total = tokenFilteredCustomers;
    return {
      mrr: active.reduce((s, c) => s + c.amount * (30 * 86400) / (c.interval || 1), 0),
      activeSubscriptions: active.length,
      totalCustomers: total.length,
      churnRate: total.length ? (cancelled.length / total.length) * 100 : 0,
      lifetimeValue: total.length ? total.reduce((s, c) => s + c.lifetime, 0) / total.length : 0,
      growthRate: 0,
    };
  }, [tokenFilteredCustomers]);

  const tokenLabel = selectedToken;

  // Metric sparkline history derived from token-filtered subscriptions
  const metricHistory = useMemo(() => buildMetricHistory(tokenFilteredCustomers), [tokenFilteredCustomers]);
  const dailyChanges = useMemo(() => computeDailyChanges(metricHistory), [metricHistory]);

  // Paginate subscriptions
  const paginatedCustomers = useMemo(() => filteredCustomers.slice(
    (subsCurrentPage - 1) * subsPerPage,
    subsCurrentPage * subsPerPage
  ), [filteredCustomers, subsCurrentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setSubsCurrentPage(1);
  }, [searchTerm, statusFilter, planFilter, subsMonth, subsYear]);

// Filter/paginate activity (memoized to avoid re-running on unrelated renders)
  const filteredActivity = useMemo(() => {
    if (!recentActivity) return [];
    const needle = activitySearch.trim().toLowerCase();
    return recentActivity.filter(activity => {
      if (!tokenFilteredIds.has(activity.subscriptionId)) return false;
      if (activityTypeFilter !== 'all' && activity.type !== activityTypeFilter) return false;
      if (planFilter !== 'all') {
        const ac = customers.find(c => c.id === activity.subscriptionId);
        if (!ac || ac.plan !== planFilter) return false;
      }
      if (activityMonth !== 'all' || activityYear !== 'all') {
        let activityDate = null;
        if (activity.date) {
          activityDate = new Date(activity.date);
        } else if (activity.timestamp) {
          const ts = parseInt(activity.timestamp);
          activityDate = new Date(isNaN(ts) ? activity.timestamp : ts * 1000);
        }
        if (activityDate && !isNaN(activityDate.getTime())) {
          if (activityMonth !== 'all' && activityDate.getMonth() !== parseInt(activityMonth)) return false;
          if (activityYear !== 'all' && activityDate.getFullYear() !== parseInt(activityYear)) return false;
        } else {
          if (activityMonth !== 'all' || activityYear !== 'all') return false;
        }
      }
      if (needle) {
        const ac = customers.find(c => c.id === activity.subscriptionId);
        const matchesId = activity.subscriptionId?.toLowerCase().includes(needle);
        const matchesPlan = ac?.plan?.toLowerCase().includes(needle);
        if (!matchesId && !matchesPlan) return false;
      }
      return true;
    });
  }, [recentActivity, tokenFilteredIds, activityTypeFilter, planFilter, activityMonth, activityYear, activitySearch, customers]);

  const paginatedActivities = useMemo(() => filteredActivity.slice(
    (activityCurrentPage - 1) * activityPerPage,
    activityCurrentPage * activityPerPage
  ), [filteredActivity, activityCurrentPage]);

  // Reset activity page when filters change
  useEffect(() => {
    setActivityCurrentPage(1);
  }, [activityTypeFilter, activityMonth, activityYear, activitySearch]);

  // Export to CSV — format compatible with Substack import (email column first, all values quoted)
  const exportToCSV = useCallback(() => {
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['email', 'wallet', 'plan', 'amount_usd', 'status', 'started', 'next_billing', 'ltv_usd', 'subscription_id'];
    const rows = filteredCustomers.map(c => [
      q(c.email ?? ''),
      q(c.wallet),
      q(c.plan),
      q(c.amount.toFixed(2)),
      q(c.status),
      q(c.startDate),
      q(c.nextBilling),
      q(c.lifetime.toFixed(2)),
      q(c.id),
    ]);
    const csvContent = [headers.map(q).join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [filteredCustomers]);

  // When a subscription detail modal opens, check on-chain whether a cancellation notice was already sent
  useEffect(() => {
    if (!selectedCustomer || selectedCustomer.status !== 'active') {
      setCancelNoticeAt(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const walletProvider = getWalletProvider();
      if (!walletProvider || !ethers.isAddress(SUBS_ADDRESS)) return;
      setCancelNoticeLoading(true);
      try {
        const provider = new ethers.BrowserProvider(walletProvider);
        const subs = new ethers.Contract(SUBS_ADDRESS, SUBS_ABI, provider);
        const ts = await subs.cancellationNoticeSentAt(selectedCustomer.id);
        if (!cancelled) setCancelNoticeAt(Number(ts) > 0 ? Number(ts) : null);
      } catch {
        if (!cancelled) setCancelNoticeAt(null);
      } finally {
        if (!cancelled) setCancelNoticeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCustomer?.id, SUBS_ADDRESS]); // eslint-disable-line react-hooks/exhaustive-deps

  // Withdraw a pending merchant cancellation notice
  const handleWithdrawCancellationNotice = useCallback(async (subscriptionId) => {
    try {
      setWithdrawingNotice(true);
      setActionError(null);
      const walletProvider = getWalletProvider();
      if (!walletProvider) throw new Error('Please connect a wallet to perform this action');
      if (!ethers.isAddress(SUBS_ADDRESS)) throw new Error('Contract not deployed on this network');
      await ensureCorrectChain(network.chainId);
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const subs = new ethers.Contract(SUBS_ADDRESS, SUBS_ABI, signer);
      const tx = await subs.withdrawCancellationNotice(subscriptionId, { gasLimit: 80000 });
      await tx.wait();
      setCancelNoticeAt(null);
      setActionSuccess('Cancellation notice withdrawn.');
      setTimeout(() => setActionSuccess(null), 4000);
    } catch (err) {
      const msg = err.message?.includes('user rejected') ? 'Transaction cancelled'
        : err.message?.includes('Only merchant') ? 'Only the merchant can withdraw the notice'
        : err.message ?? 'Failed to withdraw notice';
      setActionError(msg);
      setTimeout(() => setActionError(null), 5000);
    } finally {
      setWithdrawingNotice(false);
    }
  }, [SUBS_ADDRESS, network]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1: Send on-chain cancellation notice (merchant must wait one interval before executing)
  const handleInitiateCancellation = useCallback(async (subscriptionId) => {
    try {
      setCancelling(true);
      setActionError(null);
      setActionSuccess(null);

      const walletProvider = getWalletProvider();
      if (!walletProvider) throw new Error('Please connect a wallet to perform this action');
      if (!ethers.isAddress(SUBS_ADDRESS)) throw new Error('Contract not deployed on this network');

      await ensureCorrectChain(network.chainId);

      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const subs = new ethers.Contract(SUBS_ADDRESS, SUBS_ABI, signer);

      const tx = await subs.initiateCancellation(subscriptionId, { gasLimit: 150000 });
      const receipt = await tx.wait();
      const blockTs = (await provider.getBlock(receipt.blockNumber)).timestamp;

      setCancelNoticeAt(blockTs);
      setCancelling(false);
    } catch (err) {
      setCancelling(false);
      let errorMessage = 'Failed to initiate cancellation';
      if (err.message?.includes('user rejected')) errorMessage = 'Transaction was cancelled';
      else if (err.message?.includes('insufficient funds')) errorMessage = 'Insufficient funds for transaction';
      else if (err.message?.includes('already initiated')) errorMessage = 'Cancellation notice already sent';
      else if (err.message) errorMessage = err.message;
      setActionSuccess(null);
      setActionError(errorMessage);
      setTimeout(() => setActionError(null), 6000);
    }
  }, [network, SUBS_ADDRESS]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: Execute cancellation (only succeeds after notice period has elapsed)
  const handleCancelSubscription = useCallback(async (subscriptionId) => {
    try {
      setCancelling(true);
      // Clear any existing toasts
      setActionError(null);
      setActionSuccess(null);
      
      const walletProvider = getWalletProvider();
      if (!walletProvider) {
        throw new Error('Please connect a wallet to perform this action');
      }

      if (!ethers.isAddress(SUBS_ADDRESS)) {
        throw new Error('Contract not deployed on this network');
      }

      await ensureCorrectChain(network.chainId);

      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const subs = new ethers.Contract(SUBS_ADDRESS, SUBS_ABI, signer);

      console.log('🚫 Cancelling subscription:', subscriptionId);
      const tx = await subs.cancelSubscription(subscriptionId, { gasLimit: 150000 });
      await tx.wait();
      
      // Trigger immediate data reload
      setForceReload(prev => prev + 1);
      
      // Close modal immediately
      setSelectedCustomer(null);
      setCancelling(false);
      setCancelNoticeAt(null);

      // Clear any error, show success toast
      setActionError(null);
      setActionSuccess('Subscription cancelled successfully!');
      setTimeout(() => setActionSuccess(null), 5000);
    } catch (err) {
      console.error('Cancellation failed:', err);

      // Close modal immediately
      setSelectedCustomer(null);
      setCancelling(false);
      setCancelNoticeAt(null);
      
      // Determine error message
      let errorMessage = 'Failed to cancel subscription';
      if (err.message?.includes('user rejected')) {
        errorMessage = 'Transaction was cancelled';
      } else if (err.message?.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds for transaction';
      } else if (err.message?.includes('Only customer or merchant')) {
        errorMessage = 'Only the merchant can cancel subscriptions';
      } else if (err.message?.includes('not active')) {
        errorMessage = 'This subscription is not active';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Clear any success, show error toast
      setActionSuccess(null);
      setActionError(errorMessage);
      setTimeout(() => setActionError(null), 5000);
    }
  }, [SUBS_ADDRESS, network.chainId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refund subscriber (on-chain via Subs contract)
  const handleRefund = useCallback(async (customer) => {
    try {
      setRefundStep('refunding');
      setActionError(null);
      setActionSuccess(null);

      const walletProvider = getWalletProvider();
      if (!walletProvider) {
        throw new Error('Please connect a wallet to perform this action');
      }

      if (!ethers.isAddress(SUBS_ADDRESS) || !ethers.isAddress(USDC_ADDRESS)) {
        throw new Error('Contract not deployed on this network');
      }

      await ensureCorrectChain(network.chainId);

      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const usdc = new ethers.Contract(USDC_ADDRESS, USDC_ABI, signer);
      const subs = new ethers.Contract(SUBS_ADDRESS, SUBS_ABI, signer);

      // Detect legacy contract (no feeBps). Legacy contracts track refunds globally
      // (totalPaid/totalRefund) rather than per-subscription, and createSubscription
      // never increments totalPaid — so refunds become permanently blocked once
      // totalRefund > 0 and no processPayment calls have been made.
      try {
        const legacyCheck = new ethers.Contract(
          SUBS_ADDRESS,
          ['function totalPaid() view returns (uint256)', 'function totalRefund() view returns (uint256)'],
          provider
        );
        const [paid, refunded] = await Promise.all([legacyCheck.totalPaid(), legacyCheck.totalRefund()]);
        if (refunded > paid) {
          throw new Error('Legacy contract refund unavailable');
        }
      } catch (e) {
        if (e.message === 'Legacy contract refund unavailable') throw e;
        // feeBps() exists — current contract, per-subscription tracking is correct
      }

      // Check merchant balance — compare in base units (6 decimals) to match BigInt from contract
      const refundAmountBase = ethers.parseUnits(customer.amount.toFixed(6), 6);
      const merchantBalance = await usdc.balanceOf(merchantAddress);
      if (merchantBalance < refundAmountBase) {
        throw new Error('Insufficient merchant balance for refund');
      }

      // Ensure the Subs contract has allowance to pull from merchant wallet.
      // If not approved, prompt a separate approve tx before the refund.
      const currentAllowance = await usdc.allowance(merchantAddress, SUBS_ADDRESS);
      if (currentAllowance < refundAmountBase) {
        setRefundStep('approving');
        const approveTx = await usdc.approve(SUBS_ADDRESS, ethers.MaxUint256, { gasLimit: 100000 });
        await approveTx.wait();
        setRefundStep('refunding');
      }

      const tx = await subs.refundPayment(customer.id, refundAmountBase, { gasLimit: 150000 });
      await tx.wait();

      // Trigger immediate data reload (refund now appears via on-chain event)
      setForceReload(prev => prev + 1);

      setSelectedCustomer(null);
      setRefundStep(null);

      setActionError(null);
      setActionSuccess(`Refunded $${customer.amount.toFixed(2)} USDC successfully!`);
      setTimeout(() => setActionSuccess(null), 5000);
    } catch (err) {
      console.error('Refund failed:', err);

      setSelectedCustomer(null);
      setRefundStep(null);

      const reason = err.reason ?? err.message ?? '';
      let errorMessage = 'Failed to process refund';
      if (reason.includes('user rejected') || reason.includes('User denied')) {
        errorMessage = 'Transaction was cancelled';
      } else if (reason.includes('insufficient funds') || reason.includes('Insufficient merchant balance')) {
        errorMessage = 'Insufficient merchant balance for refund';
      } else if (reason.includes('Only merchant')) {
        errorMessage = 'Only the merchant can issue refunds';
      } else if (err.message === 'Legacy contract refund unavailable') {
        errorMessage = 'Refunds are unavailable on this network. The deployed contract has exhausted its refund capacity — redeploy using the latest contract to re-enable refunds.';
      } else if (reason.includes('Refund exceeds total amount paid') || reason.includes('Total refund') || reason.includes('amountPaid') || err.code === 'CALL_EXCEPTION') {
        errorMessage = 'Refund amount exceeds the total payments made on this subscription';
      }

      setActionSuccess(null);
      setActionError(errorMessage);
      setTimeout(() => setActionError(null), 5000);
    }
  }, [SUBS_ADDRESS, USDC_ADDRESS, merchantAddress, network.chainId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable dismiss callbacks — stable identity prevents SuccessAlert/ErrorAlert timer resets
  const dismissSuccess = useCallback(() => setActionSuccess(null), []);
  const dismissError = useCallback(() => setActionError(null), []);

  // Standalone customer payment page — no auth required.
  // Activated by a URL hash like: #payment?contract=0x...&usdc=0x...&merchant=0x...&chain=11155111
  const _hash = currentHash;
  if (_hash.startsWith('#webhook-guide')) {
    return <WebhookGuidePage />;
  }
  if (_hash.startsWith('#manage')) {
    return <ManagePortalView onBack={() => { window.location.hash = ''; }} />;
  }
  if (_hash.startsWith('#payment')) {
    const _params = _hash.includes('?') ? new URLSearchParams(_hash.split('?')[1]) : new URLSearchParams();
    const _linkData = {
      plan:     _params.get('plan'),
      features: _params.get('features'),
      price:    _params.get('price'),
      interval: _params.get('interval'),
      discount: _params.get('discount'),
      token:    _params.get('token'),
    };
    return (
      <CryptoPayPortalDirect
        subsAddress={_params.get('contract') || SUBS_ADDRESS}
        usdcAddress={_params.get('usdc') || USDC_ADDRESS}
        merchantAddress={_params.get('merchant') || merchantAddress}
        chainId={_params.get('chain') ? parseInt(_params.get('chain')) : network.chainId}
        merchantName="Crypto Subscriptions"
        onSubscriptionCreated={() => {}}
        paymentLinkData={_linkData}
      />
    );
  }

  if (!user && showLanding) {
    return <Landing onEnter={() => setShowLanding(false)} />;
  }

  if (!user) {
    return <UserAuth onAuthenticated={setUser} onBackToLanding={() => setShowLanding(true)} />;
  }

  if (view === 'payment') {
    return <PaymentPageView onBack={() => { setView('dashboard'); window.location.hash = ''; }} onSubscriptionCreated={() => setForceReload(prev => prev + 1)} merchantAddress={merchantAddress} subsAddress={SUBS_ADDRESS} usdcAddress={USDC_ADDRESS} chainId={network.chainId} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-sm text-white/50">Loading subscriptions...</div>
        </div>
      </div>
    );
  }

  if (!network.subsAddress) {
    return (
      <div className="min-h-screen bg-[#080a0f] flex items-center justify-center">
        <div className="text-center max-w-sm">
          <h2 className="text-lg font-semibold text-white mb-2">Not deployed on {network.name}</h2>
          <p className="text-sm text-white/50 mb-6">FlowSub contracts haven't been deployed to this network yet.</p>
          <button onClick={() => setSelectedNetworkId('base')} className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold rounded-xl px-5 py-2.5 text-sm transition-all">
            Switch to Base
          </button>
        </div>
      </div>
    );
  }

  if (hookError) {
    return (
      <div className="min-h-screen bg-[#080a0f] flex items-center justify-center">
        <div className="text-center max-w-sm">
          <div className="w-10 h-10 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-red-400"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
          </div>
          <h2 className="text-lg font-semibold text-white mb-2">Connection Error</h2>
          <p className="text-sm text-white/50 mb-6">{hookError}</p>
          <button
            onClick={() => setForceReload(prev => prev + 1)}
            className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold rounded-xl px-5 py-2.5 text-sm transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-fadeOut {
          animation: fadeOut 0.3s ease-out;
        }
      `}</style>


      <div className="min-h-screen bg-[#080a0f] overflow-x-hidden">
      {/* Header */}
      <div className="bg-black/50 backdrop-blur-xl border-b border-white/[0.07] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5">
          <div className="flex flex-wrap items-center justify-between gap-y-2.5">
            <div className="flex items-center gap-2.5">
              <svg width="32" height="32" viewBox="0 0 36 36" fill="none" overflow="visible" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 10 C9 4, 16 4, 21 10 C26 16, 33 16, 36 10" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
                <path d="M0 19 C6 12, 15 12, 21 19 C26 26, 32 26, 36 19" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
                <path d="M4 28 C9 22, 16 22, 21 28 C26 34, 33 34, 36 28" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
              </svg>
              <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent font-bold text-xl tracking-tight">FlowSub</span>
            </div>
            {/* Wallet pill: address | balance | logout */}
            <div className="flex items-center bg-white/[0.05] border border-white/[0.09] rounded-xl text-sm order-2 sm:order-3">
              <button
                onClick={() => merchantAddress && copyField(merchantAddress, 'header-addr')}
                className="px-2.5 sm:px-3.5 py-2 flex items-center gap-1.5 relative group hover:bg-white/[0.06] transition-colors rounded-l-xl"
                title={merchantAddress || undefined}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/40 flex-shrink-0"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                <span className="text-xs sm:text-sm text-white/70">
                  {user.email || `${merchantAddress.slice(0, 6)}...${merchantAddress.slice(-4)}`}
                </span>
                {!user.email && merchantAddress && (
                  <div className={`absolute top-full right-0 sm:left-0 sm:right-auto mt-1.5 px-2.5 py-1.5 bg-[#0e1117] border rounded-lg whitespace-nowrap text-xs font-mono pointer-events-none z-50 transition-opacity ${
                    copiedField === 'header-addr'
                      ? 'opacity-100 border-emerald-500/30 text-emerald-400'
                      : 'opacity-0 group-hover:opacity-100 border-white/[0.08] text-white'
                  }`}>
                    {copiedField === 'header-addr' ? 'Copied!' : merchantAddress}
                  </div>
                )}
              </button>
              {network.usdcAddress && (
                <>
                  <div className="w-px self-stretch bg-white/[0.09] hidden sm:block" />
                  <div className="px-3.5 py-2 hidden sm:block">
                    <USDCBalance address={merchantAddress} rpcUrl={network.rpcUrl} usdcAddress={network.usdcAddress} valueClassName="text-sm text-white/70" />
                  </div>
                </>
              )}
              <div className="w-px self-stretch bg-white/[0.09]" />
              <button
                onClick={openSettings}
                className="px-2.5 sm:px-3 py-2 hover:bg-white/[0.06] text-white/40 hover:text-white transition-all"
                title="Notification settings"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>
              <div className="w-px self-stretch bg-white/[0.09]" />
              <button
                onClick={handleLogout}
                className="px-2.5 sm:px-3 py-2 hover:bg-white/[0.06] text-white/40 hover:text-white transition-all rounded-r-xl"
                title="Logout"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </button>
            </div>
            <div className="flex flex-wrap gap-2 items-center order-3 sm:order-2 w-full sm:w-auto">
              <select
                value={selectedNetworkId}
                onChange={e => setSelectedNetworkId(e.target.value)}
                className="flex-1 sm:flex-none bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 focus:border-cyan-500/50 focus:ring-0 outline-none px-3 py-2 text-xs sm:text-sm transition-all"
              >
                {NETWORK_LIST.filter(n => n.subsAddress).map(n => (
                  <option key={n.id} value={n.id}>{n.name}{n.testnet ? ' (Testnet)' : ''}{!n.subsAddress ? ' — not deployed' : ''}</option>
                ))}
              </select>
              <button
                onClick={() => setPayLinkModalOpen(true)}
                className="flex-1 sm:flex-none bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white rounded-xl px-3 sm:px-4 py-2 text-xs sm:text-sm transition-all whitespace-nowrap"
              >
                <span className="hidden sm:inline">Create </span>Payment Link
              </button>
              <button
                onClick={() => setWidgetModalOpen(true)}
                className="flex-1 sm:flex-none bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white rounded-xl px-3 sm:px-4 py-2 text-xs sm:text-sm transition-all whitespace-nowrap"
              >
                <span className="hidden sm:inline">Create </span>Widget
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications - Fixed Position */}
      <SuccessAlert message={actionSuccess} onDismiss={dismissSuccess} />
      <ErrorAlert error={actionError} onDismiss={dismissError} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <div className="bg-white/[0.04] border border-white/[0.07] rounded-2xl p-6 flex flex-col">
            <div className="text-[10px] font-medium text-white/35 uppercase tracking-widest mb-3">Monthly Recurring Revenue</div>
            <div className="text-3xl font-semibold text-white mb-1">
              ${fmtAmt(displayMetrics.mrr)} <span className="text-sm font-normal text-white/50">{tokenLabel}</span>
            </div>
            <DeltaBadge delta={dailyChanges?.mrr} />
            {metricHistory && (
              <div className="mt-auto pt-4">
                <Sparkline
                  series={metricHistory.map(p => p.mrr)}
                  timestamps={metricHistory.map(p => p.t)}
                  color="#10b981"
                  gradientId="spark-mrr"
                  formatY={(v) => v >= 1000 ? '$' + Math.round(v / 1000) + 'k' : '$' + Math.round(v)}
                />
              </div>
            )}
          </div>

          <div className="bg-white/[0.04] border border-white/[0.07] rounded-2xl p-6 flex flex-col">
            <div className="text-[10px] font-medium text-white/35 uppercase tracking-widest mb-3">Active Subscriptions</div>
            <div className="text-3xl font-semibold text-white mb-1">
              {displayMetrics.activeSubscriptions}
            </div>
            <div className="text-white/50 text-sm">of {displayMetrics.totalCustomers} total</div>
            <DeltaBadge delta={dailyChanges?.activeSubs} />
            {metricHistory && (
              <div className="mt-auto pt-4">
                <Sparkline
                  series={metricHistory.map(p => p.activeSubs)}
                  timestamps={metricHistory.map(p => p.t)}
                  color="#06b6d4"
                  gradientId="spark-subs"
                  formatY={(v) => String(Math.round(v))}
                />
              </div>
            )}
          </div>

          <div className="bg-white/[0.04] border border-white/[0.07] rounded-2xl p-6 flex flex-col">
            <div className="text-[10px] font-medium text-white/35 uppercase tracking-widest mb-3">Avg Lifetime Value</div>
            <div className="text-3xl font-semibold text-white mb-1">
              ${fmtAmt(displayMetrics.lifetimeValue)} <span className="text-sm font-normal text-white/50">{tokenLabel}</span>
            </div>
            <div className="text-white/50 text-sm">per customer</div>
            <DeltaBadge delta={dailyChanges?.ltv} />
            {metricHistory && (
              <div className="mt-auto pt-4">
                <Sparkline
                  series={metricHistory.map(p => p.ltv)}
                  timestamps={metricHistory.map(p => p.t)}
                  color="#8b5cf6"
                  gradientId="spark-ltv"
                  formatY={(v) => v >= 1000 ? '$' + Math.round(v / 1000) + 'k' : '$' + Math.round(v)}
                />
              </div>
            )}
          </div>

          <div className="bg-white/[0.04] border border-white/[0.07] rounded-2xl p-6 flex flex-col">
            <div className="text-[10px] font-medium text-white/35 uppercase tracking-widest mb-3">Churn Rate</div>
            <div className="text-3xl font-semibold text-white mb-1">
              {displayMetrics.churnRate.toFixed(1)}%
            </div>
            <DeltaBadge delta={dailyChanges?.churnRate} invert />
            {metricHistory && (
              <div className="mt-auto pt-4">
                <Sparkline
                  series={metricHistory.map(p => p.churnRate)}
                  timestamps={metricHistory.map(p => p.t)}
                  color="#f43f5e"
                  gradientId="spark-churn"
                  formatY={(v) => Math.round(v) + '%'}
                />
              </div>
            )}
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.07] rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-white/[0.07]">
            <h3 className="text-lg font-semibold text-white mb-4">Subscriptions</h3>

            {/* Filters and Search - Moved under header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Search */}
              <div className="w-full sm:flex-1 sm:min-w-[300px]">
                <input
                  type="text"
                  placeholder="Search by subscription, customer, or plan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 placeholder:text-white/30 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-base sm:text-sm transition-all"
                />
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-base sm:text-sm transition-all"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="overdue">Overdue</option>
                </select>
                <select
                  value={subsMonth}
                  onChange={(e) => setSubsMonth(e.target.value)}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-base sm:text-sm transition-all"
                >
                <option value="all">All Months</option>
                <option value="0">January</option>
                <option value="1">February</option>
                <option value="2">March</option>
                <option value="3">April</option>
                <option value="4">May</option>
                <option value="5">June</option>
                <option value="6">July</option>
                <option value="7">August</option>
                <option value="8">September</option>
                <option value="9">October</option>
                <option value="10">November</option>
                <option value="11">December</option>
              </select>

              {/* Year Filter */}
              <select
                value={subsYear}
                onChange={(e) => setSubsYear(e.target.value)}
                className="bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-base sm:text-sm transition-all"
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={exportToCSV}
                disabled={filteredCustomers.length === 0}
                className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold rounded-xl px-4 py-2.5 text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Export CSV
              </button>


            </div>
          </div>

          {/* Active Plan Filter Chip */}
          {planFilter !== 'all' && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[10px] text-white/35 uppercase tracking-widest">Filtered by plan:</span>
              <button
                onClick={() => { setPlanFilter('all'); setSubsCurrentPage(1); }}
                className={`inline-flex items-center gap-1.5 ${getPlanColor(planFilter)}`}
              >
                {planFilter}
                <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
              </button>
            </div>
          )}

        </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-white/[0.05] bg-white/[0.02]">
                <tr>
                  <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Subscription</th>
                  <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Customer</th>
                  <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Customer Balance ({selectedToken})</th>
                  <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Plan</th>
                  <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">MRR ({selectedToken})</th>
                  <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Status</th>
                  <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Date Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {paginatedCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-white/50">
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  paginatedCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className="hover:bg-white/[0.03] transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm text-white/50">
                          {customer.id.slice(0, 6)}...{customer.id.slice(-4)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white/50">
                          {customer.wallet.slice(0, 6)}...{customer.wallet.slice(-4)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-white/70">
                        <USDCBalance address={customer.wallet} label="" rpcUrl={network.rpcUrl} usdcAddress={network.usdcAddress} hideCurrency valueClassName="text-sm text-white/70" />
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => { e.stopPropagation(); setPlanFilter(planFilter === customer.plan ? 'all' : customer.plan); setSubsCurrentPage(1); }}
                          className={`${getPlanColor(customer.plan)} ${planFilter === customer.plan ? 'ring-2 ring-offset-1 ring-cyan-500/50' : 'hover:opacity-80'} transition-opacity`}
                        >
                          {customer.plan}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-white">
                        ${fmtAmt(customer.interval > 0 ? customer.amount / (customer.interval / (30 * 24 * 3600)) : customer.amount)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={getStatusColor(customer.status)}>
                          {customer.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-white/50">
                        {customer.createdTimestamp ? (
                          (() => {
                            const d = new Date(customer.createdTimestamp * 1000);
                            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          })()
                        ) : customer.startDate}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden px-4 pt-3 pb-4 space-y-2">
            {paginatedCustomers.length === 0 ? (
              <div className="px-3 py-12 text-center text-white/50">
                No subscribers found
              </div>
            ) : (
              paginatedCustomers.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  className="rounded-xl bg-white/[0.025] active:bg-white/[0.07] transition-colors cursor-pointer p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-mono text-white/90 truncate">
                        {customer.id.slice(0, 6)}...{customer.id.slice(-4)}
                      </div>
                      <div className="text-xs text-white/35 mt-1 truncate">
                        {customer.wallet.slice(0, 6)}...{customer.wallet.slice(-4)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={getStatusColor(customer.status)}>
                        {customer.status}
                      </span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/20"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-white/[0.06]">
                    <button
                      onClick={(e) => { e.stopPropagation(); setPlanFilter(planFilter === customer.plan ? 'all' : customer.plan); setSubsCurrentPage(1); }}
                      className={`${getPlanColor(customer.plan)} ${planFilter === customer.plan ? 'ring-2 ring-offset-1 ring-cyan-500/50' : 'hover:opacity-80'} transition-opacity`}
                    >
                      {customer.plan}
                    </button>
                    <div className="text-right shrink-0">
                      <div className="text-base font-semibold text-white">
                        ${fmtAmt(customer.interval > 0 ? customer.amount / (customer.interval / (30 * 24 * 3600)) : customer.amount)}
                      </div>
                      <div className="text-[11px] text-white/30 mt-0.5">
                        {customer.createdTimestamp ? (
                          (() => {
                            const d = new Date(customer.createdTimestamp * 1000);
                            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          })()
                        ) : customer.startDate}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Results Count and Pagination Info */}
          {filteredCustomers.length > subsPerPage && (
            <div className="px-6 py-3 border-t border-white/[0.05] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 text-sm text-white/50">
              <span>
                Showing <span className="font-semibold text-white">{(subsCurrentPage - 1) * subsPerPage + 1}-{Math.min(subsCurrentPage * subsPerPage, filteredCustomers.length)}</span> of <span className="font-semibold text-white">{filteredCustomers.length}</span> subscriptions
                <span className="ml-2">
                  (Page {subsCurrentPage} of {Math.ceil(filteredCustomers.length / subsPerPage)})
                </span>
              </span>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setSubsCurrentPage(p => Math.max(1, p - 1))}
                  disabled={subsCurrentPage === 1}
                  className="flex-1 sm:flex-none px-4 py-2.5 sm:px-3 sm:py-1.5 bg-white/[0.05] hover:bg-white/[0.09] disabled:opacity-30 text-white/70 rounded-lg text-sm sm:text-xs font-medium transition-all"
                >
                  Previous
                </button>
                <button
                  onClick={() => setSubsCurrentPage(p => Math.min(Math.ceil(filteredCustomers.length / subsPerPage), p + 1))}
                  disabled={subsCurrentPage === Math.ceil(filteredCustomers.length / subsPerPage)}
                  className="flex-1 sm:flex-none px-4 py-2.5 sm:px-3 sm:py-1.5 bg-white/[0.05] hover:bg-white/[0.09] disabled:opacity-30 text-white/70 rounded-lg text-sm sm:text-xs font-medium transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white/[0.04] backdrop-blur-xl border border-white/[0.07] rounded-2xl">
          <div className="px-6 py-5 border-b border-white/[0.07]">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>

            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Search */}
              <div className="w-full sm:flex-1 sm:min-w-[300px]">
                <input
                  type="text"
                  value={activitySearch}
                  onChange={(e) => setActivitySearch(e.target.value)}
                  placeholder="Search by subscription ID or plan..."
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 placeholder:text-white/30 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-base sm:text-sm transition-all"
                />
              </div>

              {/* Filters and Export - Right Side */}
              <div className="flex items-center gap-3 flex-wrap">

                {/* Activity Type Filter */}
                <select
                  value={activityTypeFilter}
                  onChange={(e) => {
                    setActivityTypeFilter(e.target.value);
                    setActivityCurrentPage(1);
                  }}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 focus:border-cyan-500/50 focus:ring-0 outline-none px-3 py-2.5 text-base sm:text-sm transition-all"
                >
                  <option value="all">All Types</option>
                  <option value="subscription">Created</option>
                  <option value="payment">Payments</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refund">Refund</option>
                </select>

                {/* Month Filter */}
                  <select
                    value={activityMonth}
                    onChange={(e) => {
                      setActivityMonth(e.target.value);
                      setActivityCurrentPage(1);
                    }}
                    className="bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 focus:border-cyan-500/50 focus:ring-0 outline-none px-3 py-2.5 text-base sm:text-sm transition-all"
                  >
                    <option value="all">All Months</option>
                    <option value="0">January</option>
                    <option value="1">February</option>
                    <option value="2">March</option>
                    <option value="3">April</option>
                    <option value="4">May</option>
                    <option value="5">June</option>
                    <option value="6">July</option>
                    <option value="7">August</option>
                    <option value="8">September</option>
                    <option value="9">October</option>
                    <option value="10">November</option>
                    <option value="11">December</option>
                  </select>

                {/* Year Filter */}
                <select
                  value={activityYear}
                  onChange={(e) => {
                    setActivityYear(e.target.value);
                    setActivityCurrentPage(1);
                  }}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 focus:border-cyan-500/50 focus:ring-0 outline-none px-3 py-2.5 text-base sm:text-sm transition-all"
                >
                  <option value="all">All Years</option>
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                </select>

                {/* Clear Filters */}
                {(activityTypeFilter !== 'all' || activityMonth !== 'all' || activityYear !== 'all' || planFilter !== 'all' || activitySearch) && (
                  <button
                    onClick={() => {
                      setActivityTypeFilter('all');
                      setActivityMonth('all');
                      setActivityYear('all');
                      setPlanFilter('all');
                      setActivitySearch('');
                      setActivityCurrentPage(1);
                    }}
                    className="px-3 py-2 text-sm text-white/50 hover:text-white font-medium transition-all"
                  >
                    Clear Filters
                  </button>
                )}

                {/* Export CSV Button */}
                <button
                  onClick={() => {
                    const filtered = filteredActivity;
                // Prepare CSV data
                const csvRows = [
                  ['Type', 'Amount', 'Date', 'Time', 'Status']
                ];
                
                filtered.forEach(activity => {
                  const type = activity.type === 'payment' ? 'Payment' : activity.type === 'cancelled' ? 'Cancelled' : activity.type === 'refund' ? 'Refund' : 'Subscription';

                  const amount = activity.amount ? activity.amount.toFixed(2) : '-';
                  const date = activity.date || '-';
                  const time = activity.time || '-';
                  const status = activity.status || '-';
                  
                  csvRows.push([type, amount, date, time, status]);
                });
                
                // Convert to CSV string
                const csvContent = csvRows.map(row => 
                  row.map(cell => `"${cell}"`).join(',')
                ).join('\n');
                
                // Download
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold rounded-xl px-4 py-2.5 text-sm transition-all"
            >
              Export CSV
            </button>
              </div>
            </div>
          </div>
          
          <div>
            {(!recentActivity || recentActivity.length === 0) ? (
              <div className="p-8 text-center text-white/50">
                No recent activity
              </div>
            ) : filteredActivity.length === 0 ? (
              <div className="p-8 text-center text-white/50">
                No activities match the selected filters
              </div>
            ) : (
              <>
                  <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-white/[0.05] bg-white/[0.02]">
                      <tr>
                        <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Type</th>
                        <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Subscription</th>
                        <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Plan</th>
                        <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Amount ({selectedToken})</th>
                        <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Status</th>
                        <th className="text-left text-[10px] font-medium text-white/35 uppercase tracking-widest px-6 py-3.5">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.05]">
                      {paginatedActivities.map((activity) => {
                        const activityCustomer = customers.find(c => c.id === activity.subscriptionId);

                        const handleActivityClick = () => {
                          if ((activity.type === 'payment' && activityCustomer) || (activity.type === 'refund' && activityCustomer)) {
                            setSelectedPayment({
                              customer: activityCustomer,
                              amount: activity.amount,
                              date: activity.date || activity.timestamp || '-',
                              timestamp: activity.timestamp,
                              gasFee: activity.gasFee,
                              txHash: activity.txHash,
                              status: activity.status || 'success'
                            });
                          } else if (activityCustomer) {
                            setSelectedCustomer(activityCustomer);
                          }
                        };

                        return (
                          <tr
                            key={`${activity.txHash || activity.subscriptionId}-${activity.type}`}
                            onClick={handleActivityClick}
                            className={`${(activityCustomer && (activity.type === 'payment' || activity.type === 'subscription' || activity.type === 'cancelled' || activity.type === 'refund')) ? 'cursor-pointer hover:bg-white/[0.03]' : ''} transition-colors`}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {activity.type === 'payment' ? (
                                  <div className="w-7 h-7 bg-emerald-500/15 rounded-full flex items-center justify-center shrink-0">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400"><polyline points="20 6 9 17 4 12"/></svg>
                                  </div>
                                ) : activity.type === 'cancelled' ? (
                                  <div className="w-7 h-7 bg-red-500/15 rounded-full flex items-center justify-center shrink-0">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-red-400"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  </div>
                                ) : activity.type === 'refund' ? (
                                  <div className="w-7 h-7 bg-amber-500/15 rounded-full flex items-center justify-center shrink-0">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
                                  </div>
                                ) : (
                                  <div className="w-7 h-7 bg-cyan-500/15 rounded-full flex items-center justify-center shrink-0">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-400"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                  </div>
                                )}
                                <span className="text-sm text-white/50">
                                  {activity.type === 'payment' ? 'Payment' : activity.type === 'cancelled' ? 'Subscription cancelled' : activity.type === 'refund' ? 'Refund' : 'Subscription created'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-white/50">
                                {activity.subscriptionId
                                  ? `${activity.subscriptionId.slice(0, 6)}...${activity.subscriptionId.slice(-4)}`
                                  : '—'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {activityCustomer ? (
                                <button
                                  onClick={() => { setPlanFilter(planFilter === activityCustomer.plan ? 'all' : activityCustomer.plan); setActivityCurrentPage(1); }}
                                  className={`${getPlanColor(activityCustomer.plan)} ${planFilter === activityCustomer.plan ? 'ring-2 ring-offset-1 ring-cyan-500/50' : 'hover:opacity-80'} transition-opacity`}
                                >
                                  {activityCustomer.plan}
                                </button>
                              ) : (
                                <span className="text-white/50 text-sm">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {activity.amount > 0 && activity.type !== 'subscription' ? (
                                <span className={`font-semibold text-sm ${activity.type === 'refund' ? 'text-red-400' : 'text-white'}`}>
                                  {activity.type === 'refund'
                                    ? `($${fmtAmt(activity.amount)})`
                                    : `$${fmtAmt(activity.amount)}`}
                                </span>
                              ) : (
                                <span className="text-white/50 text-sm">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span className={getStatusColor(activity.status)}>
                                {activity.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-white/50">
                                {(() => {
                                  if (activity.timestamp) {
                                    try {
                                      const ts = Number(activity.timestamp);
                                      const d = new Date(ts * 1000);
                                      if (!isNaN(d.getTime())) {
                                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                          + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                      }
                                    } catch (e) {}
                                  }
                                  return activity.date || '-';
                                })()}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>

                  {/* Mobile card list */}
                  <div className="md:hidden px-4 pt-3 pb-4 space-y-2">
                    {paginatedActivities.map((activity) => {
                      const activityCustomer = customers.find(c => c.id === activity.subscriptionId);
                      const isTappable = activityCustomer && (activity.type === 'payment' || activity.type === 'subscription' || activity.type === 'cancelled' || activity.type === 'refund');

                      const handleActivityClick = () => {
                        if ((activity.type === 'payment' && activityCustomer) || (activity.type === 'refund' && activityCustomer)) {
                          setSelectedPayment({
                            customer: activityCustomer,
                            amount: activity.amount,
                            date: activity.date || activity.timestamp || '-',
                            timestamp: activity.timestamp,
                            gasFee: activity.gasFee,
                            txHash: activity.txHash,
                            status: activity.status || 'success'
                          });
                        } else if (activityCustomer) {
                          setSelectedCustomer(activityCustomer);
                        }
                      };

                      return (
                        <div
                          key={`${activity.txHash || activity.subscriptionId}-${activity.type}`}
                          onClick={isTappable ? handleActivityClick : undefined}
                          className={`rounded-xl bg-white/[0.025] transition-colors p-4 ${isTappable ? 'cursor-pointer active:bg-white/[0.07]' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              {activity.type === 'payment' ? (
                                <div className="w-7 h-7 bg-emerald-500/15 rounded-full flex items-center justify-center shrink-0">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                              ) : activity.type === 'cancelled' ? (
                                <div className="w-7 h-7 bg-red-500/15 rounded-full flex items-center justify-center shrink-0">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-red-400"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </div>
                              ) : activity.type === 'refund' ? (
                                <div className="w-7 h-7 bg-amber-500/15 rounded-full flex items-center justify-center shrink-0">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
                                </div>
                              ) : (
                                <div className="w-7 h-7 bg-cyan-500/15 rounded-full flex items-center justify-center shrink-0">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-400"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="text-sm text-white/90 truncate">
                                  {activity.type === 'payment' ? 'Payment' : activity.type === 'cancelled' ? 'Subscription cancelled' : activity.type === 'refund' ? 'Refund' : 'Subscription created'}
                                </div>
                                <div className="text-xs text-white/35 mt-0.5 truncate">
                                  {activity.subscriptionId
                                    ? `${activity.subscriptionId.slice(0, 6)}...${activity.subscriptionId.slice(-4)}`
                                    : '—'}
                                </div>
                              </div>
                            </div>
                            <span className={`shrink-0 ${getStatusColor(activity.status)}`}>
                              {activity.status}
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-white/[0.06]">
                            {activityCustomer ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); setPlanFilter(planFilter === activityCustomer.plan ? 'all' : activityCustomer.plan); setActivityCurrentPage(1); }}
                                className={`${getPlanColor(activityCustomer.plan)} ${planFilter === activityCustomer.plan ? 'ring-2 ring-offset-1 ring-cyan-500/50' : 'hover:opacity-80'} transition-opacity`}
                              >
                                {activityCustomer.plan}
                              </button>
                            ) : (
                              <span className="text-white/30 text-sm">—</span>
                            )}
                            <div className="text-right shrink-0">
                              {activity.amount > 0 && activity.type !== 'subscription' ? (
                                <div className={`font-semibold text-base ${activity.type === 'refund' ? 'text-red-400' : 'text-white'}`}>
                                  {activity.type === 'refund'
                                    ? `($${fmtAmt(activity.amount)})`
                                    : `$${fmtAmt(activity.amount)}`}
                                </div>
                              ) : (
                                <div className="text-white/30 text-sm">—</div>
                              )}
                              <div className="text-[11px] text-white/30 mt-0.5">
                                {(() => {
                                  if (activity.timestamp) {
                                    try {
                                      const ts = Number(activity.timestamp);
                                      const d = new Date(ts * 1000);
                                      if (!isNaN(d.getTime())) {
                                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                      }
                                    } catch (e) {}
                                  }
                                  return activity.date || '-';
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {filteredActivity.length > activityPerPage && (
                    <div className="px-6 py-3 border-t border-white/[0.05] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 text-sm text-white/50">
                      <span>
                        Showing <span className="font-semibold text-white">{(activityCurrentPage - 1) * activityPerPage + 1}-{Math.min(activityCurrentPage * activityPerPage, filteredActivity.length)}</span> of <span className="font-semibold text-white">{filteredActivity.length}</span> activities
                        <span className="ml-2">
                          (Page {activityCurrentPage} of {Math.ceil(filteredActivity.length / activityPerPage)})
                        </span>
                      </span>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => setActivityCurrentPage(p => Math.max(1, p - 1))}
                          disabled={activityCurrentPage === 1}
                          className="flex-1 sm:flex-none px-4 py-2.5 sm:px-3 sm:py-1.5 bg-white/[0.05] hover:bg-white/[0.09] disabled:opacity-30 text-white/70 rounded-lg text-sm sm:text-xs font-medium transition-all"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setActivityCurrentPage(p => Math.min(Math.ceil(filteredActivity.length / activityPerPage), p + 1))}
                          disabled={activityCurrentPage === Math.ceil(filteredActivity.length / activityPerPage)}
                          className="flex-1 sm:flex-none px-4 py-2.5 sm:px-3 sm:py-1.5 bg-white/[0.05] hover:bg-white/[0.09] disabled:opacity-30 text-white/70 rounded-lg text-sm sm:text-xs font-medium transition-all"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
            )}
          </div>
        </div>
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => { e.currentTarget._mbdown = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.currentTarget._mbdown) { setSelectedCustomer(null); setCancelNoticeAt(null); } }}
        >
          <div
            className="bg-[#0e1117] border border-white/[0.08] rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-7 pt-5 pb-4 border-b border-white/[0.07] sticky top-0 bg-[#0e1117] z-10 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Subscription Detail</h2>
                <button
                  onClick={() => { setSelectedCustomer(null); setCancelNoticeAt(null); }}
                  className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] rounded-lg text-white/40 hover:text-white transition-all"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
                </button>
              </div>
            </div>

            {/* Line items */}
            <div className="divide-y divide-white/[0.05]">
              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Status</span>
                <span className={getStatusColor(selectedCustomer.status)}>
                  {selectedCustomer.status}
                </span>
              </div>

              {selectedCustomer.status === 'past_due' && (
                <div className="flex items-center justify-between px-7 py-2.5 bg-amber-500/10">
                  <span className="text-[10px] text-amber-400/80 uppercase tracking-widest">Amount due</span>
                  <span className="text-sm text-white/70">${fmtAmt(selectedCustomer.amount)} {resolveTokenSymbol(selectedCustomer.tokenAddress)}</span>
                </div>
              )}

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Plan</span>
                <span className="text-sm text-white/70">{selectedCustomer.plan}</span>
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Subscription</span>
                <AddressCell address={selectedCustomer.id} fieldKey="sub-id" copiedField={copiedField} onCopy={copyField} />
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">From (Customer)</span>
                <AddressCell address={selectedCustomer.wallet} fieldKey="sub-from" copiedField={copiedField} onCopy={copyField} />
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">To (Merchant)</span>
                <AddressCell address={merchantAddress} fieldKey="sub-to" copiedField={copiedField} onCopy={copyField} />
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Customer balance</span>
                <USDCBalance address={selectedCustomer.wallet} label="" rpcUrl={network.rpcUrl} usdcAddress={network.usdcAddress} valueClassName="text-sm text-white/70" />
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">
                  {selectedCustomer.interval && selectedCustomer.interval >= 31536000 ? 'Annual amount' : 'Monthly amount'}
                </span>
                <span className="text-sm text-white/70">${fmtAmt(selectedCustomer.amount)} {resolveTokenSymbol(selectedCustomer.tokenAddress)}</span>
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Total paid</span>
                <span className="text-sm text-white/70">${fmtAmt(selectedCustomer.lifetime)} {resolveTokenSymbol(selectedCustomer.tokenAddress)}</span>
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Created</span>
                <span className="text-sm text-white/70">
                  {(selectedCustomer.createdTimestamp && fmtDateTime(selectedCustomer.createdTimestamp)) || selectedCustomer.startDate}
                </span>
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Next payment</span>
                <span className="text-sm text-white/70">
                  {(selectedCustomer.nextBillingTimestamp && fmtDateTime(selectedCustomer.nextBillingTimestamp)) || selectedCustomer.nextBilling}
                </span>
              </div>
            </div>

            {/* Footer - Actions */}
            <div className="px-7 py-4 border-t border-white/[0.07] rounded-b-2xl">
              {(selectedCustomer.status === 'active' || selectedCustomer.status === 'past_due') ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => handleRefund(selectedCustomer)}
                    disabled={refundStep !== null || selectedCustomer.payments === 0}
                    title={selectedCustomer.payments === 0 ? 'No payments to refund' : undefined}
                    className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white rounded-xl px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {refundStep !== null ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin"></div>
                        {refundStep === 'approving' ? 'Step 1/2: Approving...' : 'Step 2/2: Refunding...'}
                      </>
                    ) : (
                      'Refund Last Payment'
                    )}
                  </button>
                  {(() => {
                    const nowSec = Math.floor(Date.now() / 1000);
                    const effectiveAt = cancelNoticeAt ? cancelNoticeAt + (selectedCustomer.interval || 0) : null;
                    const noticeElapsed = effectiveAt && nowSec >= effectiveAt;

                    if (cancelNoticeLoading) {
                      return (
                        <button disabled className="flex-1 bg-red-500/10 border border-red-500/20 text-red-400/50 rounded-xl px-4 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 cursor-not-allowed">
                          <div className="w-4 h-4 border-2 border-red-400/40 border-t-transparent rounded-full animate-spin"></div>
                          Checking...
                        </button>
                      );
                    }

                    if (cancelNoticeAt && !noticeElapsed) {
                      const label = fmtDateTime(effectiveAt);
                      return (
                        <div className="flex-1 flex flex-col gap-1.5">
                          <button
                            onClick={() => handleWithdrawCancellationNotice(selectedCustomer.id)}
                            disabled={withdrawingNotice}
                            className="w-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/70 hover:text-white rounded-xl px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {withdrawingNotice ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin"></div>
                                Withdrawing...
                              </>
                            ) : (
                              'Withdraw Cancel'
                            )}
                          </button>
                          <div className="text-center text-[10px] text-white/30">Executable {label}</div>
                        </div>
                      );
                    }

                    if (cancelNoticeAt && noticeElapsed) {
                      // Notice elapsed — ready to execute
                      return (
                        <button
                          onClick={() => handleCancelSubscription(selectedCustomer.id)}
                          disabled={cancelling}
                          className="flex-1 bg-red-500/30 hover:bg-red-500/40 border border-red-500/60 text-red-300 hover:text-red-200 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {cancelling ? (
                            <>
                              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                              Processing...
                            </>
                          ) : (
                            'Execute Cancel'
                          )}
                        </button>
                      );
                    }

                    // No notice sent yet
                    return (
                      <button
                        onClick={() => handleInitiateCancellation(selectedCustomer.id)}
                        disabled={cancelling}
                        className="flex-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 hover:text-red-300 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {cancelling ? (
                          <>
                            <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                            Initiating...
                          </>
                        ) : (
                          'Initiate Cancel'
                        )}
                      </button>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-2 text-sm text-white/50">
                  This subscription has been cancelled
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Receipt Modal */}
      {selectedPayment && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => { e.currentTarget._mbdown = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.currentTarget._mbdown) setSelectedPayment(null); }}
        >
          <div
            className="bg-[#0e1117] border border-white/[0.08] rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-7 pt-5 pb-4 border-b border-white/[0.07] sticky top-0 bg-[#0e1117] z-10 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Transaction Detail</h2>
                <button
                  onClick={() => setSelectedPayment(null)}
                  className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] rounded-lg text-white/40 hover:text-white transition-all"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
                </button>
              </div>
            </div>

            {/* All line items */}
            <div className="divide-y divide-white/[0.05]">
              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Status</span>
                <span className={getStatusColor(selectedPayment.status === 'failed' ? 'failed' : 'success')}>
                  {selectedPayment.status === 'failed' ? 'failed' : 'success'}
                </span>
              </div>

              {selectedPayment.txHash && (
                <div className="flex items-center justify-between px-7 py-2.5">
                  <span className="text-[10px] text-white/35 uppercase tracking-widest">Transaction ID</span>
                  <span
                    onClick={() => copyField(selectedPayment.txHash, 'tx-id')}
                    className="text-sm text-white/70 cursor-pointer hover:text-white transition-colors"
                    title="Click to copy"
                  >
                    {copiedField === 'tx-id' ? 'Copied!' : `${selectedPayment.txHash.slice(0, 10)}...${selectedPayment.txHash.slice(-8)}`}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">From</span>
                <AddressCell
                  address={selectedPayment.type === 'refund' ? merchantAddress : selectedPayment.customer.wallet}
                  fieldKey="tx-from"
                  copiedField={copiedField}
                  onCopy={copyField}
                />
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">To</span>
                <AddressCell
                  address={selectedPayment.type === 'refund' ? selectedPayment.customer.wallet : merchantAddress}
                  fieldKey="tx-to"
                  copiedField={copiedField}
                  onCopy={copyField}
                />
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Amount</span>
                <span className="text-sm text-white/70">${fmtAmt(selectedPayment.amount)} {selectedToken}</span>
              </div>

              {selectedPayment.type === 'payment' && (
                <>
                  <div className="flex items-center justify-between px-7 py-2.5">
                    <span className="text-[10px] text-white/35 uppercase tracking-widest">Platform Fee (1.5%)</span>
                    <span className="text-sm text-white/70">
                      ${fmtAmt(selectedPayment.feeAmount ?? selectedPayment.amount * 0.015)} {selectedToken}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-7 py-2.5">
                    <span className="text-[10px] text-white/35 uppercase tracking-widest">Merchant Net</span>
                    <span className="text-sm text-white/70">
                      ${fmtAmt(selectedPayment.amount - (selectedPayment.feeAmount ?? selectedPayment.amount * 0.015))} {selectedToken}
                    </span>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Time</span>
                <span className="text-sm text-white/70">
                  {fmtDateTime(selectedPayment.timestamp) || selectedPayment.date}
                </span>
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Plan</span>
                <span className="text-sm text-white/70">{selectedPayment.customer.plan}</span>
              </div>

              {selectedPayment.customer.id && (
                <div className="flex items-center justify-between px-7 py-2.5">
                  <span className="text-[10px] text-white/35 uppercase tracking-widest">Subscription</span>
                  <AddressCell address={selectedPayment.customer.id} fieldKey="tx-sub-id" copiedField={copiedField} onCopy={copyField} />
                </div>
              )}

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Period</span>
                <span className="text-sm text-white/70">
                  {fmtPeriodRange(selectedPayment) || '—'}
                </span>
              </div>

              <div className="flex items-center justify-between px-7 py-2.5">
                <span className="text-[10px] text-white/35 uppercase tracking-widest">Gas fee</span>
                <span className="text-sm text-white/70">
                  {selectedPayment.gasFee != null
                    ? parseFloat(selectedPayment.gasFee.toFixed(8)) + ' ETH'
                    : '\u2014'}
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="px-7 py-4 border-t border-white/[0.07] rounded-b-2xl">
              <button
                onClick={() => {
                  let dateTimeStr = fmtDateTime(selectedPayment.timestamp) || selectedPayment.date;
                  let billingMonth = fmtPeriodRange(selectedPayment) || '—';

                  const gasFeeStr = selectedPayment.gasFee != null
                    ? parseFloat(selectedPayment.gasFee.toFixed(8)) + ' ETH'
                    : 'N/A';

                  const isRefund = selectedPayment.type === 'refund';
                  const receipt = `TRANSACTION DETAIL
${'='.repeat(40)}
${selectedPayment.txHash ? `\nTransaction ID:\n${selectedPayment.txHash}\n` : ''}
From${isRefund ? ' (Merchant)' : ' (Customer)'}: ${isRefund ? merchantAddress : selectedPayment.customer.wallet}
To${isRefund ? ' (Customer)' : ' (Merchant)'}:   ${isRefund ? selectedPayment.customer.wallet : merchantAddress}

Amount:       ${'$'}${fmtAmt(selectedPayment.amount)} USDC
Time:         ${dateTimeStr}
Plan:         ${selectedPayment.customer.plan}
Period:       ${billingMonth}
Gas Fee:      ${gasFeeStr}
Status:       ${selectedPayment.status}

${'='.repeat(40)}
Generated: ${new Date().toLocaleString()}`.trim();

                  const blob = new Blob([receipt], { type: 'text/plain;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `receipt-${new Date().toISOString().slice(0, 10)}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold rounded-xl px-6 py-2.5 text-sm transition-all flex items-center justify-center gap-2"
              >
                Download Receipt
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Payment Link Generator Modal */}
      {payLinkModalOpen && (
        <PayLinkGenerator
          onClose={() => setPayLinkModalOpen(false)}
          merchantAddress={merchantAddress}
          subsAddress={SUBS_ADDRESS}
          usdcAddress={USDC_ADDRESS}
          chainId={network.chainId}
        />
      )}

      {/* Widget Generator Modal */}
      {widgetModalOpen && (
        <WidgetGenerator
          onClose={() => setWidgetModalOpen(false)}
          merchantAddress={merchantAddress}
          subsAddress={SUBS_ADDRESS}
          usdcAddress={USDC_ADDRESS}
          chainId={network.chainId}
        />
      )}

      {/* Notification Settings Modal */}
      <NotificationSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        telegramChatId={settingsTelegramChatId}
        setTelegramChatId={setSettingsTelegramChatId}
        editingTelegram={settingsEditingTelegram}
        setEditingTelegram={setSettingsEditingTelegram}
        webhookUrl={settingsWebhookUrl}
        setWebhookUrl={setSettingsWebhookUrl}
        editingWebhook={settingsEditingWebhook}
        setEditingWebhook={setSettingsEditingWebhook}
        substackPublication={settingsSubstackPublication}
        setSubstackPublication={setSettingsSubstackPublication}
        substackToken={settingsSubstackToken}
        setSubstackToken={setSettingsSubstackToken}
        substackTokenValid={settingsSubstackTokenValid}
        substackLastSynced={settingsSubstackLastSynced}
        substackTesting={settingsSubstackTesting}
        substackTestResult={settingsSubstackTestResult}
        onTestSubstack={testSubstackToken}
        notifyPayments={settingsNotifyPayments}
        setNotifyPayments={setSettingsNotifyPayments}
        notifyCancels={settingsNotifyCancels}
        setNotifyCancels={setSettingsNotifyCancels}
        onSave={saveSettings}
        saving={settingsSaving}
        saved={settingsSaved}
      />
      </div>
    </>
  );
}